import {
  EventQuestionDto,
  EventQuestionType,
  ProfileFieldKey,
  SubQuestionEntry,
  SubQuestionsMap,
  UpsertEventQuestions
} from "@garther/interfaces";
import { Box, Button, Checkbox, CreateCard, Input, Spinner } from "@garther/ui/components";
import { cn } from "@garther/ui/libs";
import { Plus, RotateCcw, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { useEventQuestionsQuery, useUpsertEventQuestions } from "@/features/event/hooks/use-events-query";

interface EventQuestionsProps {
  eventId: string;
}

type LocalQuestion =
  | {
      _kind: "profile";
      profileField: ProfileFieldKey;
    }
  | {
      _kind: "custom";
      id?: string;
      label: string;
      type: EventQuestionType.TEXT | EventQuestionType.SINGLE_CHOICE | EventQuestionType.MULTIPLE_CHOICE;
      required: boolean;
      options: string[];
      subQuestions: SubQuestionsMap | null;
    };

const PROFILE_FIELD_LABELS: Record<ProfileFieldKey, string> = {
  [ProfileFieldKey.NAME]: "Name",
  [ProfileFieldKey.PHONE]: "Phone",
  [ProfileFieldKey.DATE_OF_BIRTH]: "Date of Birth",
  [ProfileFieldKey.GENDER]: "Gender",
  [ProfileFieldKey.DIETARY_RESTRICTIONS]: "Dietary Restrictions",
  [ProfileFieldKey.QUIZ]: "Quiz (Garther personality quiz)"
};

const PROFILE_FIELDS: ProfileFieldKey[] = [
  ProfileFieldKey.NAME,
  ProfileFieldKey.GENDER,
  ProfileFieldKey.DATE_OF_BIRTH,
  ProfileFieldKey.PHONE
  // ProfileFieldKey.DIETARY_RESTRICTIONS,
  // ProfileFieldKey.QUIZ
];

function questionsToLocal(questions: EventQuestionDto[]): LocalQuestion[] {
  return questions
    .filter((q) => q.type !== EventQuestionType.PROFILE_FIELD)
    .map((q) => ({
      _kind: "custom" as const,
      id: q.id,
      label: q.label,
      type: q.type as
        | EventQuestionType.TEXT
        | EventQuestionType.SINGLE_CHOICE
        | EventQuestionType.MULTIPLE_CHOICE,
      required: q.required,
      options: q.options ?? [],
      subQuestions: (q.subQuestions as SubQuestionsMap | null) ?? null
    }));
}

function buildUpsertPayload(
  profileFields: ProfileFieldKey[],
  customQuestions: Extract<LocalQuestion, { _kind: "custom" }>[]
): UpsertEventQuestions {
  const orderedProfileFields = PROFILE_FIELDS.filter((f) => profileFields.includes(f));
  const profileItems = orderedProfileFields.map((field) => ({
    type: EventQuestionType.PROFILE_FIELD,
    profileField: field,
    label: PROFILE_FIELD_LABELS[field],
    options: [] as string[],
    required: true,
    order: PROFILE_FIELDS.indexOf(field),
    subQuestions: null
  }));

  const customItems = customQuestions.map((q, i) => ({
    ...(q.id ? { id: q.id } : {}),
    type: q.type,
    profileField: null,
    label: q.label,
    options: q.options,
    required: q.required,
    order: 100 + i,
    subQuestions: q.subQuestions
  }));

  return { questions: [...profileItems, ...customItems] };
}

interface CustomQuestionCardProps {
  question: Extract<LocalQuestion, { _kind: "custom" }>;
  onChange: (updated: Extract<LocalQuestion, { _kind: "custom" }>) => void;
  onDelete: () => void;
}

function CustomQuestionCard({ question, onChange, onDelete }: CustomQuestionCardProps) {
  const [expandedSubQ, setExpandedSubQ] = useState<Set<number>>(new Set());

  const hasOptions =
    question.type === EventQuestionType.SINGLE_CHOICE || question.type === EventQuestionType.MULTIPLE_CHOICE;

  const handleOptionChange = (index: number, value: string) => {
    const oldValue = question.options[index];
    const next = [...question.options];
    next[index] = value;

    const nextSubQ = question.subQuestions ? { ...question.subQuestions } : null;
    if (nextSubQ && oldValue in nextSubQ) {
      nextSubQ[value] = nextSubQ[oldValue];
      delete nextSubQ[oldValue];
    }

    onChange({ ...question, options: next, subQuestions: nextSubQ });
  };

  const handleAddOption = () => {
    onChange({ ...question, options: [...question.options, ""] });
  };

  const handleRemoveOption = (index: number) => {
    const removedValue = question.options[index];
    const nextOptions = question.options.filter((_, i) => i !== index);

    let nextSubQ = question.subQuestions ? { ...question.subQuestions } : null;
    if (nextSubQ && removedValue in nextSubQ) {
      delete nextSubQ[removedValue];
      if (Object.keys(nextSubQ).length === 0) nextSubQ = null;
    }

    setExpandedSubQ((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });

    onChange({ ...question, options: nextOptions, subQuestions: nextSubQ });
  };

  return (
    <Box className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-3">
          <Input
            value={question.label}
            onChange={(e) => onChange({ ...question, label: e.target.value })}
            placeholder="Question label"
            className="text-responsive-base"
          />

          <div className="flex items-center gap-4">
            <select
              value={question.type}
              onChange={(e) =>
                onChange({
                  ...question,
                  type: e.target.value as
                    | EventQuestionType.TEXT
                    | EventQuestionType.SINGLE_CHOICE
                    | EventQuestionType.MULTIPLE_CHOICE,
                  options: e.target.value === EventQuestionType.TEXT ? [] : question.options
                })
              }
              className={cn(
                "flex-1 h-9 rounded-md border border-border bg-background-primary px-3 py-1",
                "text-responsive-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-primary"
              )}
            >
              <option value={EventQuestionType.TEXT}>Text</option>
              <option value={EventQuestionType.SINGLE_CHOICE}>Single Choice</option>
              <option value={EventQuestionType.MULTIPLE_CHOICE}>Multiple Choice</option>
            </select>

            <label className="flex items-center gap-2 text-responsive-sm text-text-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={question.required}
                onChange={(e) => onChange({ ...question, required: e.target.checked })}
                className="h-4 w-4 rounded border-border accent-primary"
              />
              Required
            </label>
          </div>

          {hasOptions && (
            <div className="space-y-2 pl-1">
              {question.options.map((opt, i) => {
                const isSingleChoice = question.type === EventQuestionType.SINGLE_CHOICE;
                const isExpanded = expandedSubQ.has(i);

                return (
                  <div key={i} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        value={opt}
                        onChange={(e) => handleOptionChange(i, e.target.value)}
                        placeholder={`Option ${i + 1}`}
                        className="text-responsive-sm"
                      />
                      {isSingleChoice && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedSubQ((prev) => {
                              const next = new Set(prev);
                              if (next.has(i)) next.delete(i);
                              else next.add(i);
                              return next;
                            })
                          }
                          className={cn(
                            "text-responsive-xs shrink-0 px-1.5 py-0.5 rounded border transition-colors",
                            isExpanded || (question.subQuestions?.[opt] ?? []).length > 0
                              ? "border-primary text-primary"
                              : "border-border text-text-tertiary hover:text-primary hover:border-primary"
                          )}
                          title="Add sub-question"
                        >
                          {(question.subQuestions?.[opt] ?? []).length > 0 && !isExpanded
                            ? `sub-Q (${(question.subQuestions?.[opt] ?? []).length})`
                            : "sub-Q"}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveOption(i)}
                        className="text-text-tertiary hover:text-red-500 transition-colors shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>

                    {isSingleChoice && isExpanded && (
                      <div className="pl-4 border-l-2 border-border space-y-2">
                        {(question.subQuestions?.[opt] ?? []).map(
                          (subQ: SubQuestionEntry, subIdx: number) => (
                            <div key={subIdx} className="flex items-center gap-2">
                              <Input
                                value={subQ.label}
                                onChange={(e) => {
                                  const list = [...(question.subQuestions?.[opt] ?? [])];
                                  list[subIdx] = {
                                    ...subQ,
                                    label: e.target.value
                                  };
                                  const nextSubQ = {
                                    ...(question.subQuestions ?? {}),
                                    [opt]: list
                                  };
                                  onChange({
                                    ...question,
                                    subQuestions: nextSubQ
                                  });
                                }}
                                placeholder={`Sub-question ${subIdx + 1}`}
                                className="text-responsive-sm flex-1"
                              />
                              <label className="flex items-center gap-1 text-responsive-sm text-text-secondary cursor-pointer select-none shrink-0">
                                <input
                                  type="checkbox"
                                  checked={subQ.required}
                                  onChange={(e) => {
                                    const list = [...(question.subQuestions?.[opt] ?? [])];
                                    list[subIdx] = {
                                      ...subQ,
                                      required: e.target.checked
                                    };
                                    const nextSubQ = {
                                      ...(question.subQuestions ?? {}),
                                      [opt]: list
                                    };
                                    onChange({
                                      ...question,
                                      subQuestions: nextSubQ
                                    });
                                  }}
                                  className="h-3 w-3 rounded border-border accent-primary"
                                />
                                Req
                              </label>
                              <button
                                type="button"
                                onClick={() => {
                                  const list = (question.subQuestions?.[opt] ?? []).filter(
                                    (_: SubQuestionEntry, j: number) => j !== subIdx
                                  );
                                  const nextSubQ = {
                                    ...(question.subQuestions ?? {})
                                  };
                                  if (list.length > 0) {
                                    nextSubQ[opt] = list;
                                  } else {
                                    delete nextSubQ[opt];
                                  }
                                  onChange({
                                    ...question,
                                    subQuestions: Object.keys(nextSubQ).length > 0 ? nextSubQ : null
                                  });
                                }}
                                className="text-text-tertiary hover:text-red-500 transition-colors shrink-0"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          )
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            const list = [
                              ...(question.subQuestions?.[opt] ?? []),
                              { label: "", required: false }
                            ];
                            const nextSubQ = {
                              ...(question.subQuestions ?? {}),
                              [opt]: list
                            };
                            onChange({ ...question, subQuestions: nextSubQ });
                          }}
                          className="flex items-center gap-1 text-responsive-xs text-primary hover:text-primary/80 transition-colors"
                        >
                          <Plus className="h-3 w-3" />
                          Add sub-question
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <button
                type="button"
                onClick={handleAddOption}
                className="flex items-center gap-1 text-responsive-sm text-primary hover:text-primary/80 transition-colors"
              >
                <Plus className="h-3 w-3" />
                Add option
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onDelete}
          className="text-text-tertiary hover:text-red-500 transition-colors mt-1 shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </Box>
  );
}

export function EventQuestions({ eventId }: EventQuestionsProps) {
  const { data: serverQuestions, isLoading } = useEventQuestionsQuery(eventId);

  const serverProfileFields = useMemo(
    () =>
      (serverQuestions ?? [])
        .filter((q) => q.type === EventQuestionType.PROFILE_FIELD && q.profileField)
        .map((q) => q.profileField as ProfileFieldKey),
    [serverQuestions]
  );

  const serverCustomQuestions = useMemo(
    () => questionsToLocal(serverQuestions ?? []) as Extract<LocalQuestion, { _kind: "custom" }>[],
    [serverQuestions]
  );

  const [enabledProfileFields, setEnabledProfileFields] = useState<ProfileFieldKey[]>([]);
  const [customQuestions, setCustomQuestions] = useState<Extract<LocalQuestion, { _kind: "custom" }>[]>([]);

  useEffect(() => {
    if (!serverQuestions) return;
    setEnabledProfileFields(serverProfileFields);
    setCustomQuestions(serverCustomQuestions);
  }, [serverQuestions]);

  const hasChanges = useMemo(() => {
    if (enabledProfileFields.length !== serverProfileFields.length) return true;
    if (enabledProfileFields.some((f, i) => f !== serverProfileFields[i])) return true;
    if (customQuestions.length !== serverCustomQuestions.length) return true;
    return customQuestions.some((q, i) => {
      const s = serverCustomQuestions[i];
      return (
        q.label !== s.label ||
        q.type !== s.type ||
        q.required !== s.required ||
        JSON.stringify(q.options) !== JSON.stringify(s.options) ||
        JSON.stringify(q.subQuestions) !== JSON.stringify(s.subQuestions)
      );
    });
  }, [enabledProfileFields, customQuestions, serverProfileFields, serverCustomQuestions]);

  const { mutate: upsertQuestions, isPending: isSaving } = useUpsertEventQuestions();

  const handleToggleProfileField = (field: ProfileFieldKey) => {
    const checked = !enabledProfileFields.includes(field);
    setEnabledProfileFields((prev) => (checked ? [...prev, field] : prev.filter((f) => f !== field)));
  };

  const handleAddQuestion = () => {
    setCustomQuestions((prev) => [
      ...prev,
      {
        _kind: "custom",
        label: "",
        type: EventQuestionType.TEXT,
        required: true,
        options: [],
        subQuestions: null
      }
    ]);
  };

  const handleChangeQuestion = (index: number, updated: Extract<LocalQuestion, { _kind: "custom" }>) => {
    setCustomQuestions((prev) => prev.map((q, i) => (i === index ? updated : q)));
  };

  const handleDeleteQuestion = (index: number) => {
    setCustomQuestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleReset = () => {
    setEnabledProfileFields(serverProfileFields);
    setCustomQuestions(serverCustomQuestions);
  };

  const handleSave = () => {
    upsertQuestions({
      eventId,
      data: buildUpsertPayload(enabledProfileFields, customQuestions)
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-responsive-xl font-light text-text-primary uppercase font-literata mb-1">
            Registration Questions
          </p>
          <p className="text-responsive-base font-normal text-text-secondary">
            Collect information from attendees at registration.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {hasChanges && (
            <Button variant="outline" onClick={handleReset} disabled={isSaving}>
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          )}
          <Button onClick={handleSave} disabled={isSaving || !hasChanges}>
            {isSaving ? <Spinner className="w-4 h-4 mr-2" /> : null}
            Save
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <h3 className="text-responsive-md font-light text-text-secondary uppercase px-1 font-literata pl-4 md:pl-6">
          Profile Fields
        </h3>
        <Box className="grid grid-cols-2 gap-4">
          {PROFILE_FIELDS.map((field) => (
            <label
              key={field}
              className="flex items-center gap-3 cursor-pointer select-none group"
              onClick={() => handleToggleProfileField(field)}
            >
              <Checkbox
                isChecked={enabledProfileFields.includes(field)}
                className="shrink-0 group-hover:border-border-dark"
              />
              <span className="text-responsive-base text-text-primary">{PROFILE_FIELD_LABELS[field]}</span>
            </label>
          ))}
        </Box>
      </div>

      <div className="space-y-2">
        <h3 className="text-responsive-md font-light text-text-secondary uppercase px-1 font-literata pl-4 md:pl-6">
          Custom Questions
        </h3>

        {customQuestions.length === 0 ? (
          <Box>
            <p className="text-responsive-base text-text-secondary text-center py-4">
              No custom questions yet. Add one below.
            </p>
          </Box>
        ) : (
          <div className="space-y-3">
            {customQuestions.map((q, i) => (
              <CustomQuestionCard
                key={i}
                question={q}
                onChange={(updated) => handleChangeQuestion(i, updated)}
                onDelete={() => handleDeleteQuestion(i)}
              />
            ))}
          </div>
        )}

        <CreateCard
          className="min-h-[100px] w-full"
          title="Add Question"
          subtitle="Add questions for your attendees to answer when they book the event"
          onClick={handleAddQuestion}
        />
      </div>
    </div>
  );
}
